import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, Search, Wrench, ArrowLeft, Package, AlertTriangle, Trash2 } from 'lucide-react';

const TOOL_CATEGORIES = ['POWER_TOOL', 'HAND_TOOL', 'MEASURING', 'SAFETY', 'TESTING', 'CABLE', 'LIFTING', 'OTHER'];
const TOOL_STATUSES = ['IN_STORE', 'ISSUED', 'IN_SERVICE', 'DISPOSED'];
const TX_TYPES = ['ISSUE', 'RETURN', 'SERVICE_SENT', 'SERVICE_RECEIVED', 'DAMAGE_REPORT', 'DISPOSAL'];

export default function ToolsPage() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const canDelete = ['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT'].includes(user?.role);

  const deleteTool = async (toolId, toolName) => {
    if (!window.confirm(`Delete ${toolName}? This will move it to Deleted Items.`)) return;
    try {
      await api.delete(`/tools/${toolId}`);
      fetchTools();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const fetchTools = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (search) params.set('search', search);
    api.get(`/tools?${params}`).then(r => setTools(r.data.tools || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTools(); api.get('/tools/dashboard/summary').then(r => setSummary(r.data)); }, []);
  useEffect(() => { fetchTools(); }, [filterStatus, search]);

  const canCreate = ['DIRECTOR'].includes(user?.role);

  const viewTool = async (toolId) => {
    try {
      const res = await api.get(`/tools/${toolId}`);
      setSelectedTool(res.data.tool);
    } catch (err) { alert('Failed'); }
  };

  if (selectedTool) {
    return <ToolDetail tool={selectedTool} onBack={() => setSelectedTool(null)} onRefresh={() => viewTool(selectedTool.id)} />;
  }

  return (
    <div data-testid="tools-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Tools & Inventory</h1>
          <p className="text-sm text-slate-500">{tools.length} tools registered</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="add-tool-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> Add Tool
          </button>
        )}
        <button onClick={() => setShowRequests(true)} data-testid="tool-requests-btn"
          className="border border-[#1B5486] text-[#1B5486] px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1B5486]/5 flex items-center gap-2">
          Requests
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={summary.total || 0} color="text-slate-900" />
        <StatCard label="In Store" value={summary.in_store || 0} color="text-green-600" />
        <StatCard label="Issued" value={summary.issued || 0} color="text-blue-600" />
        <StatCard label="In Service" value={summary.in_service || 0} color="text-amber-600" />
        <StatCard label="Damaged" value={summary.damaged || 0} color="text-red-600" />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tools..."
            className="border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm outline-none w-56" data-testid="search-tools" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Statuses</option>
          {TOOL_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Code</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Make/Model</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Condition</th>
              {canDelete && <th className="w-10"></th>}
            </tr></thead>
            <tbody>
              {tools.map(t => (
                <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => viewTool(t.id)} data-testid={`tool-row-${t.id}`}>
                  <td className="py-2.5 px-4 font-mono text-xs text-[#1B5486] font-medium">{t.tool_code}</td>
                  <td className="py-2.5 px-4 text-slate-900 font-medium">{t.name}</td>
                  <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(t.category)}</span></td>
                  <td className="py-2.5 px-4 text-slate-500 text-xs">{[t.make, t.model].filter(Boolean).join(' ') || '-'}</td>
                  <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${t.current_status === 'IN_STORE' ? 'bg-green-100 text-green-700' : t.current_status === 'ISSUED' ? 'bg-blue-100 text-blue-700' : t.current_status === 'IN_SERVICE' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{formatStatus(t.current_status)}</span></td>
                  <td className="py-2.5 px-4"><span className={`text-xs ${t.condition === 'GOOD' ? 'text-green-600' : t.condition === 'DAMAGED' ? 'text-red-600' : 'text-amber-600'}`}>{formatStatus(t.condition)}</span></td>
                  {canDelete && <td className="py-2.5 px-2"><button onClick={e => { e.stopPropagation(); deleteTool(t.id, t.name); }} data-testid={`delete-tool-${t.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {tools.length === 0 && <div className="text-center py-12 text-slate-400">No tools found</div>}
        </div>
      )}

      {showCreate && <CreateToolModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchTools(); }} />}

      {/* S3: Tool Request Tab (visible on separate view) */}
      {showRequests && <ToolRequestPanel user={user} onClose={() => setShowRequests(false)} />}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function ToolDetail({ tool, onBack, onRefresh }) {
  const { user } = useAuth();
  const [showTx, setShowTx] = useState(false);
  const [txForm, setTxForm] = useState({ type: 'ISSUE', employee_id: '', project_id: '', remarks: '' });
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects || []));
    api.get('/employees').then(r => setEmployees(r.data.employees || [])).catch(() => {});
  }, []);

  const createTx = async () => {
    try {
      await api.post(`/tools/${tool.id}/transaction`, txForm);
      setShowTx(false);
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="tool-detail" className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 font-heading">{tool.name}</h1>
          <p className="text-sm text-slate-500">{tool.tool_code} | {formatStatus(tool.category)}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${tool.current_status === 'IN_STORE' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
          {formatStatus(tool.current_status)}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Make</span><span>{tool.make || '-'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Model</span><span>{tool.model || '-'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Serial No</span><span>{tool.serial_number || '-'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Condition</span><span className={tool.condition === 'GOOD' ? 'text-green-600' : 'text-red-600'}>{formatStatus(tool.condition)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Purchase Date</span><span>{formatDate(tool.purchase_date) || '-'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Purchase Cost</span><span>{tool.purchase_cost ? formatINRFull(tool.purchase_cost) : '-'}</span></div>
            {tool.current_holder && <div className="flex justify-between"><span className="text-slate-500">Current Holder</span><span className="text-[#1B5486] font-medium">{tool.current_holder.name}</span></div>}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Actions</h3>
          </div>
          {user?.role === 'DIRECTOR' && (
            <div className="space-y-2">
              <button onClick={() => { setTxForm({...txForm, type: 'ISSUE'}); setShowTx(true); }} className="w-full text-left px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100">Issue to Employee</button>
              <button onClick={() => { setTxForm({...txForm, type: 'RETURN'}); setShowTx(true); }} className="w-full text-left px-3 py-2 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100">Return to Store</button>
              <button onClick={() => { setTxForm({...txForm, type: 'SERVICE_SENT'}); setShowTx(true); }} className="w-full text-left px-3 py-2 text-sm bg-amber-50 text-amber-700 rounded hover:bg-amber-100">Send for Service</button>
              <button onClick={() => { setTxForm({...txForm, type: 'DAMAGE_REPORT'}); setShowTx(true); }} className="w-full text-left px-3 py-2 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100">Report Damage</button>
            </div>
          )}
        </div>
      </div>

      {tool.transactions?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Transaction History</h3>
          <div className="space-y-2">
            {tool.transactions.map(tx => (
              <div key={tx.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded text-sm">
                <span className={`text-xs px-2 py-0.5 rounded-full ${tx.type === 'ISSUE' ? 'bg-blue-100 text-blue-700' : tx.type === 'RETURN' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{formatStatus(tx.type)}</span>
                <span className="text-slate-500 text-xs">{formatDate(tx.date)}</span>
                <span className="text-slate-700 flex-1">{tx.remarks || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowTx(false)}>
          <div className="bg-white rounded-lg border w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="tool-tx-modal">
            <h2 className="text-lg font-semibold mb-4">{formatStatus(txForm.type)}</h2>
            <div className="space-y-3">
              {(txForm.type === 'ISSUE') && (
                <>
                  <select value={txForm.employee_id} onChange={e => setTxForm({...txForm, employee_id: e.target.value})} className="input-field w-full">
                    <option value="">Select Employee</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
                  </select>
                  <select value={txForm.project_id} onChange={e => setTxForm({...txForm, project_id: e.target.value})} className="input-field w-full">
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
                  </select>
                </>
              )}
              <textarea placeholder="Remarks" value={txForm.remarks} onChange={e => setTxForm({...txForm, remarks: e.target.value})} className="input-field w-full" rows={2} />
              <div className="flex gap-3">
                <button onClick={() => setShowTx(false)} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
                <button onClick={createTx} className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium">Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateToolModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', category: 'POWER_TOOL', make: '', model: '', serial_number: '', purchase_date: '', purchase_cost: '', condition: 'GOOD', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/tools', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-tool-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Add Tool</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field w-full" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field w-full">
                {TOOL_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Condition</label>
              <select value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="input-field w-full">
                {['GOOD', 'FAIR', 'POOR'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Make</label>
              <input value={form.make} onChange={e => setForm({...form, make: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Model</label>
              <input value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Serial Number</label>
            <input value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} className="input-field w-full" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Purchase Date</label>
              <input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Purchase Cost</label>
              <input type="number" value={form.purchase_cost} onChange={e => setForm({...form, purchase_cost: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-tool-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Add Tool'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// S3: Tool Request Workflow
function ToolRequestPanel({ user, onClose }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tool_name: '', quantity: 1, purpose: '', project_id: '', urgency: 'NORMAL' });
  const [projects, setProjects] = useState([]);
  const canApprove = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);

  useEffect(() => {
    api.get('/tools/requests').then(r => setRequests(r.data.requests || [])).catch(() => {});
    api.get('/projects').then(r => setProjects(r.data.projects || [])).catch(() => {});
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tools/requests', form);
      const r = await api.get('/tools/requests');
      setRequests(r.data.requests || []);
      setShowForm(false);
      setForm({ tool_name: '', quantity: 1, purpose: '', project_id: '', urgency: 'NORMAL' });
    } catch (err) { alert('Failed to submit request'); }
  };

  const handleAction = async (reqId, action) => {
    try {
      await api.patch(`/tools/requests/${reqId}`, { status: action });
      const r = await api.get('/tools/requests');
      setRequests(r.data.requests || []);
    } catch (err) { alert('Action failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg border w-full sm:max-w-2xl max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="tool-requests-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 font-heading">Tool Requests</h2>
          <div className="flex gap-2">
            <button onClick={() => setShowForm(!showForm)} className="text-xs px-3 py-1.5 bg-[#1B5486] text-white rounded-md" data-testid="new-tool-request-btn">
              {showForm ? 'Cancel' : '+ New Request'}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={submitRequest} className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Tool Name *</label>
              <input value={form.tool_name} onChange={e => setForm({...form, tool_name: e.target.value})} className="input-field w-full" required /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
              <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: +e.target.value})} className="input-field w-full" min="1" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Project</label>
              <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="input-field w-full">
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
              </select></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Urgency</label>
              <select value={form.urgency} onChange={e => setForm({...form, urgency: e.target.value})} className="input-field w-full">
                <option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option>
              </select></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">Purpose</label>
              <input value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} className="input-field w-full" /></div>
            <div className="col-span-2 flex justify-end">
              <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium" data-testid="submit-tool-request-btn">Submit Request</button>
            </div>
          </form>
        )}

        {requests.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No tool requests</p> : (
          <div className="space-y-3">
            {requests.map(req => (
              <div key={req.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`tool-request-${req.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{req.tool_name} x{req.quantity}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${req.urgency === 'URGENT' ? 'bg-red-100 text-red-700' : req.urgency === 'HIGH' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{req.urgency}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${req.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : req.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{req.status}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{req.purpose || '-'} | By {req.requested_by_name || 'Unknown'} | {formatDate(req.created_at)}</p>
                </div>
                {canApprove && req.status === 'PENDING' && (
                  <div className="flex gap-1">
                    <button onClick={() => handleAction(req.id, 'APPROVED')} data-testid={`approve-request-${req.id}`} className="px-2 py-1 text-xs bg-green-600 text-white rounded">Approve</button>
                    <button onClick={() => handleAction(req.id, 'REJECTED')} data-testid={`reject-request-${req.id}`} className="px-2 py-1 text-xs bg-red-600 text-white rounded">Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
